import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileSearch, Loader2, CheckCircle, XCircle } from 'lucide-react';
import apiClient from '../../lib/apiClient';

/**
 * DetailJobsToggle — Toggle for enabling/disabling detail (PO) scraping jobs
 *
 * When OFF: Only bulk export jobs run (faster, preferred)
 * When ON: Individual PO detail scraping jobs also run
 */
function DetailJobsToggle() {
  const queryClient = useQueryClient();

  // Fetch current status
  const { data: config, isLoading, error } = useQuery({
    queryKey: ['scraper', 'config', 'detail-jobs'],
    queryFn: () => apiClient.get('/api/scraper/config/detail-jobs'),
    refetchInterval: 30000,
  });

  // Toggle mutation — the switch flips the instant it's clicked (optimistic,
  // UI-responsiveness review 2026-08-08); a failed write rolls it back.
  const toggleMutation = useMutation({
    mutationFn: (enabled) => apiClient.post('/api/scraper/config/detail-jobs', { enabled }),
    onMutate: async (enabled) => {
      await queryClient.cancelQueries({ queryKey: ['scraper', 'config', 'detail-jobs'] });
      const previous = queryClient.getQueryData(['scraper', 'config', 'detail-jobs']);
      queryClient.setQueryData(['scraper', 'config', 'detail-jobs'], (prev) => ({ ...(prev || {}), enabled }));
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['scraper', 'config', 'detail-jobs'], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper', 'config', 'detail-jobs'] });
    },
  });

  const handleToggle = () => {
    const currentValue = config?.enabled ?? false;
    toggleMutation.mutate(!currentValue);
  };

  if (isLoading) {
    return (
      <div className="sd-detail-toggle sd-detail-loading">
        <Loader2 size={14} className="sd-spin" />
        <span>Detail Jobs</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sd-detail-toggle sd-detail-error" title={error.message}>
        <FileSearch size={14} />
        <span>Error</span>
      </div>
    );
  }

  const isEnabled = config?.enabled ?? false;

  return (
    <div className={`sd-detail-toggle ${isEnabled ? 'sd-detail-on' : 'sd-detail-off'}`}>
      <button
        className="sd-detail-btn"
        onClick={handleToggle}
        title={isEnabled
          ? "Detail jobs enabled. Click to disable (bulk export only)"
          : "Detail jobs disabled. Click to enable individual PO scraping"
        }
      >
        <span className="sd-detail-icon">
          <FileSearch size={14} />
        </span>
        <span className="sd-detail-label">Detail Jobs</span>
        <span className={`sd-detail-state ${isEnabled ? 'is-on' : 'is-off'}`}>
          {isEnabled ? 'ON' : 'OFF'}
        </span>
        <span className="sd-detail-indicator">
          {isEnabled ? <CheckCircle size={12} /> : <XCircle size={12} />}
        </span>
      </button>
    </div>
  );
}

export default DetailJobsToggle;
